/**
 * Shopify Inventory Service
 * 
 * This service handles all inventory-related operations including tracking,
 * quantity updates, and location management for Shopify products.
 */

import { ShopifyGraphQLService } from './shopifyGraphQLService';

/**
 * Get locations query
 * @type {string}
 */
const locationsQuery = `
  query {
    locations(first: 10) {
      edges {
        node {
          id
          name
          address {
            formatted
          }
        }
      }
    }
  }
`;

/**
 * Get inventory levels query
 * @type {string}
 */
const inventoryLevelsQuery = `
  query getInventoryLevels($id: ID!) {
    inventoryItem(id: $id) {
      id
      tracked
      inventoryLevels(first: 10) {
        edges {
          node {
            id
            available
            incoming
            location {
              id
              name
              address {
                formatted
              }
            }
          }
        }
      }
    }
  }
`;

export class ShopifyInventoryService {
  constructor(shop, accessToken) {
    this.graphqlService = new ShopifyGraphQLService(shop, accessToken);
  }

  /**
   * Updates inventory tracking for an inventory item
   * @param {string} inventoryItemId - The ID of the inventory item
   * @param {boolean} trackingEnabled - Whether to enable inventory tracking
   * @returns {Promise<object>} Updated inventory item data
   */
  async updateInventoryTracking(inventoryItemId, trackingEnabled = true) {
    const updateInventoryTrackingMutation = `
      mutation inventoryItemUpdate($id: ID!, $input: InventoryItemInput!) {
        inventoryItemUpdate(id: $id, input: $input) {
          inventoryItem {
            id
            tracked
          }
          userErrors {
            field
            message
          }
        }
      }
    `;

    const variables = {
      id: inventoryItemId,
      input: {
        tracked: trackingEnabled
      }
    };

    const result = await this.graphqlService.mutate(updateInventoryTrackingMutation, variables);

    if (result?.inventoryItemUpdate?.userErrors?.length > 0) {
      const errors = result.inventoryItemUpdate.userErrors;
      throw new Error(`Failed to update inventory tracking: ${JSON.stringify(errors)}`);
    }

    return result?.inventoryItemUpdate?.inventoryItem;
  }

  /**
   * Updates inventory quantity for a variant at a specific location
   * @param {string} inventoryItemId - The ID of the inventory item
   * @param {string} locationId - The ID of the location
   * @param {number} quantity - The new quantity
   * @returns {Promise<object>} Updated inventory level data
   */
  async updateInventoryQuantity(inventoryItemId, locationId, quantity) {
    // Ensure quantity is a valid number
    const parsedQuantity = parseInt(quantity, 10);
    if (isNaN(parsedQuantity) || parsedQuantity < 0) {
      throw new Error('Invalid quantity for inventory update');
    }

    // First, ensure inventory tracking is enabled
    await this.updateInventoryTracking(inventoryItemId, true);

    const updateInventoryMutation = `
      mutation inventorySetOnHandQuantities($input: InventorySetOnHandQuantitiesInput!) {
        inventorySetOnHandQuantities(input: $input) {
          userErrors {
            field
            message
          }
          inventoryAdjustmentGroup {
            createdAt
            reason
            changes {
              name
              delta
            }
          }
        }
      }
    `;

    const variables = {
      input: {
        reason: "correction",
        setQuantities: [
          {
            inventoryItemId,
            locationId,
            quantity: parsedQuantity
          }
        ]
      }
    };

    const result = await this.graphqlService.mutate(updateInventoryMutation, variables);

    if (result?.inventorySetOnHandQuantities?.userErrors?.length > 0) {
      const errors = result.inventorySetOnHandQuantities.userErrors;
      throw new Error(`Failed to update inventory quantity: ${JSON.stringify(errors)}`);
    }

    return result?.inventorySetOnHandQuantities?.inventoryAdjustmentGroup;
  }

  /**
   * Gets the default location for inventory
   * @returns {Promise<string>} The ID of the default location
   */
  async getDefaultLocation() {
    const result = await this.graphqlService.query(locationsQuery);
    const locations = result?.locations?.edges || [];

    if (locations.length === 0) {
      throw new Error('No locations found');
    }

    // Just use the first location - simpler and more reliable
    return locations[0].node.id;
  }

  /**
   * Gets inventory levels for an inventory item across all locations
   * @param {string} inventoryItemId - The ID of the inventory item
   * @returns {Promise<Array>} Array of inventory levels
   */
  async getInventoryLevels(inventoryItemId) {
    const result = await this.graphqlService.query(inventoryLevelsQuery, { id: inventoryItemId });
    const inventoryItem = result?.inventoryItem;

    if (!inventoryItem) {
      throw new Error('Inventory item not found');
    }

    return inventoryItem.inventoryLevels.edges.map(edge => edge.node);
  }

  /**
   * Adjusts inventory quantity by a relative amount
   * @param {string} inventoryItemId - The ID of the inventory item
   * @param {string} locationId - The ID of the location
   * @param {number} adjustment - The quantity to adjust by (positive or negative)
   * @returns {Promise<object>} Updated inventory level data
   */
  async adjustInventoryQuantity(inventoryItemId, locationId, adjustment) {
    const adjustInventoryMutation = `
      mutation inventoryAdjustQuantity($input: InventoryAdjustQuantityInput!) {
        inventoryAdjustQuantity(input: $input) {
          inventoryLevel {
            id
            available
          }
          userErrors {
            field
            message
          }
        }
      }
    `;

    const variables = {
      input: {
        inventoryItemId,
        locationId,
        availableDelta: adjustment
      }
    };

    const result = await this.graphqlService.mutate(adjustInventoryMutation, variables);

    if (result?.inventoryAdjustQuantity?.userErrors?.length > 0) {
      const errors = result.inventoryAdjustQuantity.userErrors;
      throw new Error(`Failed to adjust inventory quantity: ${JSON.stringify(errors)}`);
    }

    return result?.inventoryAdjustQuantity?.inventoryLevel;
  }
}
